import React, { useState, useEffect, useCallback, useRef } from 'react'
import RichWinningResultEditor, { getWinningResultRichHtml } from './RichWinningResultEditor'
import useStore from '../hooks/useStore'

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
]

const LockIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
)

function CanvasPanel() {
  const {
    goal, wScore, cScore, tScore,
    monthlyGoal, selectedMonthNumber, selectedYear,
    timerSec, timerMax, timerRunning, timerStartedAt, timerEndAt, activeDur, blocksToday,
    focusMinutesTotal, tScored,
    wShipped, loopBlocker, tomorrowSeed, closedAt, loopClosed,
    updateFormData, setTimer, addBlock, logFocusMinutes,
    loadMonthlyGoal, saveMonthlyGoal, setMonthlySelection,
    loadTodayLoop, loadDayData, closeLoop, saveGoalDraft, saveLoopDraft, saveFocusDraft,
    showToast, syncState, syncText
  } = useStore()

  const todayISO = new Date().toISOString().split('T')[0]
  const monthlyRichKey = `monthly-${selectedYear}-${selectedMonthNumber}`
  const draftTimer = useRef(null)
  const [selectedDay, setSelectedDay] = useState(todayISO)
  const [blockLog, setBlockLog] = useState([])
  const [monthlySaving, setMonthlySaving] = useState(false)
  const [focusFS, setFocusFS] = useState(false)
  const [expandedCell, setExpandedCell] = useState(null)
  const [loopUnlocked, setLoopUnlocked] = useState(false)
  const [loopSaving, setLoopSaving] = useState(false)
  const [loopEditMode, setLoopEditMode] = useState(false)
  const [isAfter5PM, setIsAfter5PM] = useState(new Date().getHours() >= 17)

  const isToday = selectedDay === todayISO
  const rScore = (wScore * cScore) + tScore
  const isLocked = !loopUnlocked && !isAfter5PM && !loopClosed

  const changeDay = useCallback((delta) => {
    const [y, m, d] = selectedDay.split('-').map(Number)
    const date = new Date(y, m - 1, d)
    date.setDate(date.getDate() + delta)
    const next = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-')
    if (next > todayISO) return
    setSelectedDay(next)
    setLoopUnlocked(false)
    setLoopEditMode(false)
  }, [selectedDay, todayISO])

  const formatNavDay = (iso) => {
    const d = new Date(iso + 'T00:00:00')
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()
  }

  const scheduleGoalSave = useCallback(() => {
    clearTimeout(draftTimer.current)
    draftTimer.current = setTimeout(() => saveGoalDraft(), 800)
  }, [saveGoalDraft])

  const scheduleDraftSave = useCallback(() => {
    clearTimeout(draftTimer.current)
    draftTimer.current = setTimeout(() => saveLoopDraft(), 800)
  }, [saveLoopDraft])

  // Load on mount
  useEffect(() => { loadMonthlyGoal() }, [loadMonthlyGoal])
  useEffect(() => () => clearTimeout(draftTimer.current), [])

  // Reload whenever selected day changes (including initial mount)
  useEffect(() => { loadDayData(selectedDay) }, [selectedDay, loadDayData])

  // Time check every minute (auto-unlock at 5 PM)
  useEffect(() => {
    if (isAfter5PM) return
    const iv = setInterval(() => {
      const after = new Date().getHours() >= 17
      if (after) setIsAfter5PM(true)
    }, 60000)
    return () => clearInterval(iv)
  }, [isAfter5PM])

  // Timer tick
  useEffect(() => {
    let iv
    if (timerRunning && timerEndAt) {
      const syncTimer = () => {
        const remaining = Math.max(0, Math.ceil((timerEndAt - Date.now()) / 1000))
        setTimer({ timerSec: remaining })

        if (remaining <= 0) {
          const completedMins = Math.round(useStore.getState().timerMax / 60)
          setTimer({
            timerRunning: false,
            timerSec: useStore.getState().timerMax,
            timerStartedAt: null,
            timerEndAt: null,
          })
          addBlock(completedMins)
          saveFocusDraft()
          const now = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          setBlockLog(p => [{ time: now, label: useStore.getState().activeDur + '-min block', type: 'Auto' }, ...p])
          showToast('🎯', 'Block complete!')
          setFocusFS(false)
        }
      }

      syncTimer()
      iv = setInterval(syncTimer, 1000)
      document.addEventListener('visibilitychange', syncTimer)

      return () => {
        clearInterval(iv)
        document.removeEventListener('visibilitychange', syncTimer)
      }
    }
    return () => {}
  }, [timerRunning, timerEndAt, setTimer, addBlock, showToast, saveFocusDraft])

  const fmt = s => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`
  const toggleTimer = () => {
    const starting = !timerRunning
    if (starting) {
      const now = Date.now()
      setTimer({ timerRunning: true, timerStartedAt: now, timerEndAt: now + timerSec * 1000 })
    } else {
      const remaining = timerEndAt ? Math.max(0, Math.ceil((timerEndAt - Date.now()) / 1000)) : timerSec
      setTimer({ timerRunning: false, timerSec: remaining, timerStartedAt: null, timerEndAt: null })
    }
    if (starting) setFocusFS(true)
  }
  const resetTimer  = () => {
    const elapsedSeconds = timerStartedAt ? Math.floor((Date.now() - timerStartedAt) / 1000) : timerMax - timerSec
    const minsRan = Math.floor(Math.max(0, Math.min(timerMax, elapsedSeconds)) / 60)
    if (minsRan > 0) {
      logFocusMinutes(minsRan)
      saveFocusDraft()
    }
    setTimer({ timerRunning: false, timerSec: timerMax, timerStartedAt: null, timerEndAt: null })
  }
  const setDur = m => setTimer({ activeDur: m, timerMax: m*60, timerSec: m*60, timerRunning: false, timerStartedAt: null, timerEndAt: null })

  const commitAndFocus = () => {
    if (!goal.trim()) { showToast('⚠', 'Write your W goal first!'); return }
    saveGoalDraft()
    const now = Date.now()
    setTimer({ timerRunning: true, timerStartedAt: now, timerEndAt: now + timerSec * 1000 })
    setFocusFS(true)
  }

  const changeMonth = useCallback((delta) => {
    let m = selectedMonthNumber + delta, y = selectedYear
    if (m > 12) { m = 1; y++ }
    if (m < 1)  { m = 12; y-- }
    setMonthlySelection({ monthNumber: m, year: y })
    loadMonthlyGoal({ monthNumber: m, year: y })
  }, [selectedMonthNumber, selectedYear, setMonthlySelection, loadMonthlyGoal])

  const handleSaveMonthly = async () => {
    setMonthlySaving(true)
    try { await saveMonthlyGoal(); showToast('✓', 'Monthly goal saved!') }
    catch { showToast('✗', 'Save failed') }
    finally { setMonthlySaving(false) }
  }

  const handleCloseLoop = async () => {
    if (!wShipped) { showToast('⚠', 'Select whether the W shipped first.'); return }
    setLoopSaving(true)
    try {
      await closeLoop()
      setLoopEditMode(false)
      showToast('✓', 'Day closed. Well done.')
    } catch {
      showToast('✗', 'Save failed — check Supabase columns.')
    } finally {
      setLoopSaving(false)
    }
  }

  const today = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()
  const ringR = 52
  const circumference = 2 * Math.PI * ringR

  const closedTimeStr = closedAt
    ? new Date(closedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : ''

  // Progress bar segments
  const progressSegs = [
    { active: !!monthlyGoal.trim(), color: 'var(--primary)' },
    { active: !!goal.trim(), color: 'var(--accent)' },
    { active: blocksToday > 0, color: 'var(--success)' },
    { active: loopClosed, color: 'var(--primary2)' },
  ]

  const SHIP_LABELS = { yes: '✓  Yes', partial: '~  Partial', no: '✗  No' }
  const SHIP_RESPONSES = {
    yes: 'Clean close. Stack another one tomorrow.',
    partial: "Progress counts. Name what's left in tomorrow's seed.",
    no: "No judgment. Name the blocker so it doesn't repeat."
  }

  return (
    <div className="canvas-page">

      {/* ── IMMERSIVE FOCUS FULLSCREEN ── */}
      {focusFS && (
        <div className="canvas-fs-overlay">
          <button className="canvas-fs-minimize" onClick={() => setFocusFS(false)} title="Minimize">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M3 8h10"/>
            </svg>
            Minimize
          </button>
          <div className="canvas-fs-inner">
            <div className="canvas-fs-timer-panel">
              <div className="canvas-fs-ring-wrap">
                <svg width="240" height="240" viewBox="0 0 240 240" style={{transform:'rotate(-90deg)'}}>
                  <circle cx="120" cy="120" r={ringR*2} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8"/>
                  <circle cx="120" cy="120" r={ringR*2} fill="none"
                    stroke={timerSec < 300 ? 'var(--coral2)' : 'var(--sage2)'}
                    strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={circumference * 2}
                    strokeDashoffset={(circumference * 2) * (1 - timerSec / timerMax)}
                    style={{filter:'drop-shadow(0 0 12px rgba(110,228,192,0.6))',transition:'stroke-dashoffset 1s linear,stroke 0.5s'}}
                  />
                </svg>
                <div className="canvas-fs-time-wrap">
                  <div className={`canvas-fs-time${timerSec < 300 ? ' warn' : ''}`}>{fmt(timerSec)}</div>
                  <div className="canvas-fs-status">{timerRunning ? 'IN PROGRESS' : 'PAUSED'}</div>
                </div>
              </div>
              <div className="canvas-fs-actions">
                <button className="btn bg-sage" style={{padding:'14px 36px',fontSize:'15px'}} onClick={toggleTimer}>
                  {timerRunning ? '⏸  Pause' : '▶  Resume'}
                </button>
                <button className="btn bg-ghost" style={{padding:'14px 20px',fontSize:'14px'}} onClick={resetTimer}>↺ Reset</button>
              </div>
            </div>
            <div className="canvas-fs-result-panel">
              <div className="canvas-fs-kicker">Today's Winning Result</div>
              {goal ? (
                <div
                  className="canvas-fs-goal-text"
                  dangerouslySetInnerHTML={{ __html: getWinningResultRichHtml(`today-${todayISO}`, goal) }}
                />
              ) : (
                <div className="canvas-fs-goal-text">No W goal set</div>
              )}
              <div className="canvas-fs-blocks">{blocksToday} block{blocksToday !== 1 ? 's' : ''} completed today</div>
            </div>
            <div className="canvas-fs-motivation">
              Key to getting what you want is the willingness to do whatever it takes
            </div>
          </div>
        </div>
      )}

      {/* ── EXPAND: Monthly Result ── */}
      {expandedCell === 'monthly' && (
        <div className="canvas-expand-overlay">
          <div className="canvas-expand-inner">
            <div className="canvas-expand-header">
              <div className="canvas-expand-title">
                <span className="canvas-step-num">01</span>&nbsp; Monthly Focus
              </div>
              <div className="canvas-month-nav" style={{margin:0}}>
                <button className="canvas-month-btn" onClick={() => changeMonth(-1)}>‹</button>
                <div className="canvas-month-pill">{MONTH_NAMES[selectedMonthNumber - 1]} {selectedYear}</div>
                <button className="canvas-month-btn" onClick={() => changeMonth(1)}>›</button>
              </div>
              <button className="canvas-expand-close" onClick={() => setExpandedCell(null)}>✕ Close</button>
            </div>
            <div className="canvas-expand-body">
              <RichWinningResultEditor
                storageKey={monthlyRichKey}
                placeholder="What result must this month produce?"
                value={monthlyGoal}
                onChange={(text) => updateFormData({ monthlyGoal: text })}
                minHeight={300}
                autoFocus
              />
            </div>
            <div className="canvas-expand-footer">
              <button className="btn bp" style={{flex:1,justifyContent:'center'}}
                onClick={async () => { await handleSaveMonthly(); setExpandedCell(null) }}
                disabled={monthlySaving}>
                {monthlySaving ? 'Saving…' : '↑  Save Monthly Goal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EXPAND: Today's W Goal ── */}
      {expandedCell === 'w' && (
        <div className="canvas-expand-overlay">
          <div className="canvas-expand-inner">
            <div className="canvas-expand-header">
              <div className="canvas-expand-title"><span className="canvas-step-num">02</span>&nbsp; Today's Winning Result</div>
              <button className="canvas-expand-close" onClick={() => setExpandedCell(null)}>✕ Close</button>
            </div>
            <div className="canvas-expand-body">
              <div className="canvas-w-hint">What result makes today a win?</div>
              <RichWinningResultEditor
                storageKey={`today-${todayISO}`}
                placeholder="e.g. Send outreach to 5 SaaS founders with tailored messages — all follow-ups scheduled."
                value={goal}
                onChange={text => { updateFormData({ goal: text }); scheduleGoalSave() }}
                minHeight={260}
                autoFocus
              />
              <div className="sw">
                <div className="sh"><span className="sq">W Clarity</span><span className="canvas-w-score">{wScore}</span></div>
                <div className="st">
                  <div className="sf2 azure" style={{width:`${wScore*10}%`}}></div>
                  <input type="range" min="1" max="10" value={wScore} className="ar"
                    onChange={e => { updateFormData({ wScore: parseInt(e.target.value) }); scheduleGoalSave() }} />
                </div>
                <div className="slbls"><span>Vague</span><span>Crystal clear</span></div>
              </div>
            </div>
            <div className="canvas-expand-footer">
              <button className="btn bg-ghost" style={{padding:'0 20px'}} onClick={() => setExpandedCell(null)}>Done</button>
              <button className="btn bp" style={{flex:1,justifyContent:'center'}}
                onClick={() => { setExpandedCell(null); commitAndFocus() }}>
                ✓ &nbsp;Commit &amp; Start Full Focus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EXPAND: Concentration & Time ── */}
      {expandedCell === 'ct' && (
        <div className="canvas-expand-overlay">
          <div className="canvas-expand-inner">
            <div className="canvas-expand-header">
              <div className="canvas-expand-title">Concentration &amp; Time</div>
              <button className="canvas-expand-close" onClick={() => setExpandedCell(null)}>✕ Close</button>
            </div>
            <div className="canvas-expand-body">
              <div className="canvas-ct-card" style={{marginBottom:'16px'}}>
                <div className="c-lbl" style={{fontSize:'10px',marginBottom:'8px',letterSpacing:'0.15em'}}>C — Concentration (auto)</div>
                <div className="sh"><span className="sq">From focus timer</span><span className="sn sage" style={{fontSize:'32px'}}>{cScore}</span></div>
                <div className="st" style={{pointerEvents:'none',opacity:0.7}}>
                  <div className="sf2 sage" style={{width:`${cScore*10}%`}}></div>
                  <input type="range" min="0" max="10" value={cScore} className="sr" readOnly onChange={() => {}} />
                </div>
                <div className="slbls"><span>{focusMinutesTotal}min logged</span><span>360min = 10</span></div>
              </div>
              <div className="canvas-ct-card">
                <div className="c-lbl" style={{fontSize:'10px',marginBottom:'8px',letterSpacing:'0.15em'}}>T — Time Well Spent</div>
                <div className="sh"><span className="sq">Health, rest, balance</span><span className="sn coral" style={{fontSize:'32px'}}>{tScore}</span></div>
                <div className="st">
                  <div className="sf2 coral" style={{width:`${tScore*10}%`}}></div>
                  <input type="range" min="1" max="10" value={tScore} className="cr"
                    onChange={e => updateFormData({ tScore: parseInt(e.target.value) })} />
                </div>
                <div className="slbls"><span>Neglected</span><span>Fully balanced</span></div>
              </div>
            </div>
            <div className="canvas-expand-footer">
              <button className="btn bg-ghost" style={{padding:'0 20px'}} onClick={() => setExpandedCell(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* ── BODY ── */}
      <div className="canvas-body">
        <div className="canvas-top-grid">

          {/* COL 1 — Monthly Result */}
          <div className="canvas-cell canvas-cell-col">
            <div className="canvas-cell-label">
              <span className="canvas-step-num">01</span>
              Monthly Focus
              <div className="canvas-month-inline">
                <button className="canvas-month-btn" onClick={e => { e.stopPropagation(); changeMonth(-1) }}>‹</button>
                <span className="canvas-month-lbl">{MONTH_NAMES[selectedMonthNumber - 1]} {selectedYear}</span>
                <button className="canvas-month-btn" onClick={e => { e.stopPropagation(); changeMonth(1) }}>›</button>
              </div>
              <button className="canvas-expand-btn" onClick={() => setExpandedCell('monthly')} title="Expand">⤢</button>
            </div>
            <div className="canvas-preview-wrap" onClick={() => setExpandedCell('monthly')}>
              {monthlyGoal ? (
                <div
                  className="canvas-preview-text"
                  dangerouslySetInnerHTML={{ __html: getWinningResultRichHtml(monthlyRichKey, monthlyGoal) }}
                />
              ) : (
                <div className="canvas-preview-text">
                  <span className="canvas-preview-ph">What result must this month produce?</span>
                </div>
              )}
              <div className="canvas-preview-badge">Edit ↗</div>
            </div>
            <div className="canvas-cell-footer">
              <button className="btn bp canvas-action-btn" onClick={handleSaveMonthly} disabled={monthlySaving}>
                {monthlySaving ? 'Saving…' : '↑  Save Monthly Goal'}
              </button>
            </div>
          </div>

          {/* COL 2 — Today's W (read-only for past days) */}
          <div className="canvas-cell canvas-cell-col">
            <div className="canvas-cell-label">
              <span className="canvas-step-num">02</span>
              {isToday ? "Today's Winning Result" : "Winning Result"}
              <div className="canvas-month-inline">
                <button className="canvas-month-btn" onClick={e => { e.stopPropagation(); changeDay(-1) }}>‹</button>
                <span className={`canvas-month-lbl${isToday ? ' canvas-day-today-lbl' : ''}`}>
                  {isToday ? 'TODAY' : formatNavDay(selectedDay)}
                </span>
                <button className="canvas-month-btn"
                  onClick={e => { e.stopPropagation(); changeDay(1) }}
                  disabled={selectedDay >= todayISO}
                  style={{opacity: selectedDay >= todayISO ? 0.3 : 1}}>›</button>
              </div>
              {isToday && <button className="canvas-expand-btn" onClick={() => setExpandedCell('w')} title="Expand">⤢</button>}
            </div>
            <div className={`canvas-preview-wrap${isToday ? '' : ' canvas-preview-readonly'}`}
              onClick={isToday ? () => setExpandedCell('w') : undefined}>
              {goal ? (
                <div
                  className="canvas-preview-text"
                  dangerouslySetInnerHTML={{ __html: getWinningResultRichHtml(`today-${selectedDay}`, goal) }}
                />
              ) : (
                <div className="canvas-preview-text">
                  <span className="canvas-preview-ph">{isToday ? 'What result makes today a win?' : 'No goal recorded for this day.'}</span>
                </div>
              )}
              {isToday && <div className="canvas-preview-badge">Edit ↗</div>}
            </div>
            <div className="canvas-w-clarity-row">
              <span className="canvas-w-clarity-lbl">W Clarity</span>
              <span className="canvas-w-score">{wScore}</span>
            </div>
            {isToday && (
              <div className="canvas-cell-footer">
                <button className="btn bp canvas-action-btn" onClick={commitAndFocus}>
                  ✓ &nbsp;Commit &amp; Start Full Focus
                </button>
              </div>
            )}
          </div>

          {/* COL 3 — Focus Timer */}
          <div className={`canvas-cell canvas-cell-col${!isToday ? ' canvas-cell-past' : ''}`}>
            <div className="canvas-cell-label">
              <span className="canvas-step-num">03</span>
              Focus Timer
            </div>
            <div className="canvas-timer-ring-wrap">
              <div className="canvas-timer-ring-inner">
                <svg width="180" height="180" viewBox="0 0 180 180" style={{transform:'rotate(-90deg)',display:'block',width:'100%',height:'100%'}}>
                  <circle cx="90" cy="90" r="76" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6"/>
                  <circle cx="90" cy="90" r="76" fill="none"
                    stroke={timerSec < 300 ? 'var(--coral2)' : 'var(--sage2)'}
                    strokeWidth="6" strokeLinecap="round"
                    strokeDasharray="478"
                    strokeDashoffset={478 * (1 - timerSec / timerMax)}
                    style={{filter:'drop-shadow(0 0 8px rgba(110,228,192,0.55))',transition:'stroke-dashoffset 1s linear,stroke 0.5s'}}
                  />
                </svg>
                <div className="canvas-timer-overlay">
                  <div className={`canvas-timer-time${timerSec < 300 ? ' warn' : ''}`}>{fmt(timerSec)}</div>
                  <div className="canvas-timer-status">{timerRunning ? 'RUNNING' : timerSec === timerMax ? 'READY' : 'PAUSED'}</div>
                </div>
              </div>
            </div>
            <div className="canvas-dur-btns">
              {[90,60,45,25].map(m => (
                <button key={m} className={`canvas-dur-btn${activeDur===m?' active':''}`} onClick={() => setDur(m)}>{m}m</button>
              ))}
            </div>
            <div className="canvas-blocks-count">
              Blocks today: <span>{blocksToday}</span>
              <span className="canvas-c-auto"> · C: {focusMinutesTotal > 0 ? <span className="azure">{cScore}</span> : <span className="eq-dim">—</span>}</span>
            </div>
            <div className="canvas-cell-footer">
              <div className="canvas-timer-actions">
                <button className="btn bg-sage canvas-action-btn" onClick={toggleTimer} style={{flex:1}}>
                  {timerRunning ? '⏸  Pause' : '▶  Start'}
                </button>
                <button className="btn bg-ghost" style={{padding:'0 16px'}} onClick={resetTimer}>↺</button>
                <button className="btn bg-ghost" style={{padding:'0 16px'}}
                  onClick={() => {
                    if (!timerRunning) {
                      const now = Date.now()
                      setTimer({ timerRunning: true, timerStartedAt: now, timerEndAt: now + timerSec * 1000 })
                    }
                    setFocusFS(true)
                  }} title="Full screen">↗</button>
              </div>
            </div>
          </div>

          {/* COL 4 — Close The Loop */}
          <div className={`canvas-cell canvas-cell-col${isLocked ? '' : ' canvas-loop-unlocked'}${!isToday ? ' canvas-cell-past' : ''}`}>
            <div className="canvas-cell-label">
              <span className="canvas-step-num">04</span>
              Close The Loop
            </div>

            {isLocked ? (
              /* LOCKED STATE */
              <div className="canvas-loop-locked">
                <div className="canvas-loop-lock-ico"><LockIcon /></div>
                <div className="canvas-loop-lock-msg">Available after 5:00 PM</div>
                <div className="canvas-loop-lock-sub">Complete your focus blocks, then return to close the day.</div>
                <button className="canvas-loop-unlock-early" onClick={() => setLoopUnlocked(true)}>
                  Unlock early →
                </button>
              </div>
            ) : (loopClosed && !loopEditMode) ? (
              /* CLOSED / SUMMARY STATE */
              <div className="canvas-loop-summary">
                <div className="canvas-loop-summary-row">
                  <span className={`canvas-loop-ship-badge ${wShipped}`}>
                    {wShipped === 'yes' ? '✓ Shipped' : wShipped === 'partial' ? '~ Partial' : '✗ Not today'}
                  </span>
                  <span className="canvas-loop-closed-time">Day closed · {closedTimeStr}</span>
                </div>
                {loopBlocker && (
                  <div className="canvas-loop-summary-field">
                    <span className="canvas-loop-summary-lbl">Blocked by</span>
                    <span className="canvas-loop-summary-val">{loopBlocker}</span>
                  </div>
                )}
                {tomorrowSeed && (
                  <div className="canvas-loop-summary-field">
                    <span className="canvas-loop-summary-lbl">Tomorrow</span>
                    <span className="canvas-loop-summary-val">{tomorrowSeed}</span>
                  </div>
                )}
                <div className="canvas-loop-done-row">
                  <span className="canvas-loop-done-check">✓</span>
                  <span className="canvas-loop-done-text">Day fully closed</span>
                  <button className="canvas-loop-edit-btn" onClick={() => setLoopEditMode(true)}>Edit</button>
                </div>
              </div>
            ) : (
              /* INTERACTIVE STATE */
              <>
                <div className="canvas-loop-fields">

                  {/* Element 1 — Did W ship? */}
                  <div className="canvas-loop-section">
                    <div className="canvas-loop-field-lbl">Did the W ship?</div>
                    <div className="canvas-loop-pills">
                      {['yes','partial','no'].map(val => (
                        <button key={val}
                          className={`canvas-loop-pill ${val}${wShipped === val ? ' active' : ''}`}
                          onClick={() => updateFormData({ wShipped: val })}>
                          {SHIP_LABELS[val]}
                        </button>
                      ))}
                    </div>
                    {wShipped && (
                      <div className={`canvas-loop-response ${wShipped}`}>
                        {SHIP_RESPONSES[wShipped]}
                      </div>
                    )}
                  </div>

                  {/* Element 2 — T scorer */}
                  <div className="canvas-loop-section canvas-loop-divider">
                    <div className="canvas-loop-field-lbl">
                      T — Time Well Spent
                      {!tScored && <span className="canvas-loop-optional">tap to score</span>}
                    </div>
                    <div className="canvas-loop-t-scorer">
                      {[1,2,3,4,5,6,7,8,9,10].map(n => (
                        <button key={n}
                          className={`canvas-loop-t-dot${tScore >= n && tScored ? ' active' : ''}`}
                          onClick={() => updateFormData({ tScore: n, tScored: true })}
                          title={n} />
                      ))}
                      <span className="canvas-loop-t-val">{tScored ? tScore : '—'}</span>
                    </div>
                    <div className="canvas-loop-micro">Beyond your W — health, learning, rest.</div>
                  </div>

                  {/* Element 3 — Blocker */}
                  <div className="canvas-loop-section canvas-loop-divider">
                    <div className="canvas-loop-field-lbl">
                      What blocked me?
                      <span className="canvas-loop-optional">optional</span>
                    </div>
                    <textarea className="canvas-loop-input"
                      placeholder="e.g. Slack rabbit hole, energy crash"
                      value={loopBlocker}
                      onChange={e => { updateFormData({ loopBlocker: e.target.value }); scheduleDraftSave() }}
                      maxLength={300} rows={2} />
                  </div>

                  {/* Element 4 — Tomorrow seed */}
                  <div className="canvas-loop-section">
                    <div className="canvas-loop-field-lbl">
                      Tomorrow's W seed
                      <span className="canvas-loop-optional">optional</span>
                    </div>
                    <textarea className="canvas-loop-input"
                      placeholder="Plant tomorrow's W here"
                      value={tomorrowSeed}
                      onChange={e => { updateFormData({ tomorrowSeed: e.target.value }); scheduleDraftSave() }}
                      maxLength={300} rows={2} />
                  </div>

                </div>{/* end canvas-loop-fields */}

                {/* Element 5 — Close Today */}
                <div className="canvas-cell-footer">
                  <button className="btn bp canvas-action-btn" onClick={handleCloseLoop} disabled={loopSaving}>
                    {loopSaving ? 'Closing…' : '✓  Close Today'}
                  </button>
                </div>
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}

export default CanvasPanel
